import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "payments";

export function usePaymentList(orderId: string) {
	return useQuery({
		queryKey: [KEY, "list", orderId],
		queryFn: () =>
			api.get<{ items: Payment[]; total: number }>(
				`/api/payments?orderId=${orderId}&limit=200&offset=0`,
			),
		enabled: !!orderId,
	});
}

export function useCreatePayment() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreatePaymentInput) => api.post<Payment>("/api/payments", data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: ["orders", "detail", vars.orderId] });
		},
	});
}

export function useUpdatePayment() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreatePaymentInput> }) =>
			api.patch<Payment>(`/api/payments/${id}`, data),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: ["orders", "detail"] });
		},
	});
}

export function useDeletePayment() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/payments/${id}`),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: ["orders", "detail"] });
		},
	});
}

interface Payment {
	id: string;
	amount: string;
	currency: string | null;
	exchangeRate: string | null;
	method: "cash" | "bank_transfer" | "credit_card" | "check" | "letter_of_credit" | "other";
	reference: string | null;
	notes: string | null;
	receivedAt: string | null;
	orderId: string;
	recordedBy: string | null;
	recordedByName: string | null;
	createdAt: string;
	updatedAt: string;
}

interface CreatePaymentInput {
	orderId: string;
	amount: number;
	currency?: string | undefined;
	exchangeRate?: number | undefined;
	method: "cash" | "bank_transfer" | "credit_card" | "check" | "letter_of_credit" | "other";
	reference?: string | undefined;
	notes?: string | undefined;
	receivedAt?: string | undefined;
}
